'use client'
import { Box, Pagination } from '@achieve/ascend'
import { useEffect, useState } from 'react'
import { LinkButton, MediaImageStandard, Typography } from 'components/Contentful'
import classNames from 'classnames'
import { Pill } from 'components/Pill'
import { ChevronRight } from 'react-feather'
import Section from 'components/Section'
import { AchieveLink } from 'components/AchieveLink'
import { getSlug } from 'utils/conversions'
import { LEARN_ROUTE } from 'constants/navigation'
import { mainProductConfig } from 'constants/mainProductConfig'
import {} from 'react'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'

function AuthorArticlesPaginated({ authorName, className, articlesAuthor }) {
  const { replace, asPath, query } = useAppRouter()
  const currentPage = Number(query.page) || 1

  const [articlesData, setArticlesData] = useState(articlesAuthor)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (currentPage === 1) {
      setArticlesData(articlesAuthor)
    }
  }, [currentPage, articlesAuthor])

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true)
      const params = new URLSearchParams({ page: String(currentPage), authorName })
      const res = await fetch(`/api/author-articles/?${params.toString()}`)

      if (res.ok) {
        const json = await res.json()
        setArticlesData(json)
      }

      setLoading(false)
    }

    // Only fetch when page param changes (not on first render)
    if (currentPage !== 1) {
      fetchArticles()
    }
  }, [currentPage, authorName])

  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'
  // Get the current URL search params, pathname, and page number with the useSearchParams and usePathname hooks
  // On pagination event, update the URL with the new page number from the Pagination component
  // And then replace the URL with the new page number and fetch for new authors data via server side
  const handleChangePage = (_, page) => {
    const params = new URLSearchParams(query)
    if (page) params.set('page', page)
    replace(`${asPath}?${params.toString()}`)
  }

  const makeItemArticle = ({ mainImage, title, hook, primaryCategory, slug }, idx) => {
    const primaryCategorySlug = getSlug(primaryCategory)
    const trackArticle = {
      list_name: 'Card Article',
      click_position: idx,
      click_id: `${mainProductConfig[siteEnv || 'achieve']?.['MainProduct']} | Read the article`,
      click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProduct']} | Read the article`,
      nav_link_section: 'Content Grid',
      track_event: 'page_nav_click',
      event_action: 'link_click',
      event_type: 'link_click',
    }

    const urlBase = LEARN_ROUTE
    const linkHrefArticle = `${urlBase}/${primaryCategorySlug}/${slug}`

    return (
      <Box
        className={'flex flex-col gap-8 max-w-[335px] md:flex-row md:max-w-[initial]'}
        data-testid="article-list-item"
        key={idx}
      >
        <Box
          className={
            'w-full max-w-[335px] h-[135px]! bg-neutral-50 rounded-[10px] [&_span]:w-full [&_span]:max-w-[335px] [&_span]:h-[135px] [&_span]:bg-neutral-50 [&_span]:rounded-[10px] md:min-w-[300px] md:max-w-[300px] md:h-[200px]! md:[&_span]:min-w-[300px] md:[&_span]:max-w-[300px] md:[&_span]:h-[200px]!'
          }
          data-testid="article-photo-container"
        >
          {mainImage?.mediaContent && (
            <AchieveLink track={trackArticle} href={linkHrefArticle}>
              <MediaImageStandard
                content={mainImage?.mediaContent}
                objectFit="cover"
                objectPosition="top center"
                layout="responsive"
                width={355}
                height={135}
                alt={'Article Photo of '}
                className={'rounded-[10px]'}
                data-testid="article-photo"
              />
            </AchieveLink>
          )}
        </Box>
        <Box
          className={'flex flex-col gap-3 md:max-w-(--breakpoint-sm)'}
          data-testid="article-info-container"
        >
          <Box className={'flex flex-col gap-3'}>
            <Box
              className={'[&_span]:font-medium [&_span]:text-[15px] [&>span]:px-1 [&>span]:py-0'}
              data-testid="article-pill-container"
            >
              <Pill
                className={'m-0'}
                category={primaryCategory}
                id={'category-article'}
                article
                pathOverride={`${urlBase}/`}
              />
            </Box>
            <AchieveLink
              track={trackArticle}
              href={linkHrefArticle}
              data-testid="author-article-title-anchor"
              className={'text-inherit no-underline'}
            >
              <Typography
                content={title}
                className="bodyLg lg:headingMd font-medium!"
                data-testid="author-article-title"
              />
            </AchieveLink>
          </Box>
          {hook && <Typography content={hook} variant="bodySm" fontWeight="regular" />}
          <Box className={'[&_button]:hover:no-underline!'} data-testid="read-more-container">
            <LinkButton
              track={trackArticle}
              content={{ linkHref: linkHrefArticle, linkText: 'Read more' }}
              typographicOptions={{ variant: 'bodySm', fontWeight: 'bold' }}
              variant="text"
              endIcon={<ChevronRight />}
              className={'p-0 [&_span]:text-[15px] [&_span]:ml-0 [&_svg]:w-[15px] [&_svg]:h-[15px]'}
            />
          </Box>
        </Box>
      </Box>
    )
  }

  const renderArticlesList = () => {
    return articlesData?.articles?.map((item, idx) => makeItemArticle(item, idx))
  }

  return (
    <Section
      className={classNames('px-0 py-12', className)}
      childContainerClassName={'w-full flex flex-col gap-12'}
      data-testid="author-articles-paginated-section"
    >
      <Typography
        content={`${authorName} articles`}
        className="headingMd lg:displayLg font-medium! text-center"
      />
      <Box className={'flex flex-col gap-8 items-center md:[align-items:initial] md:gap-12'}>
        {renderArticlesList()}
      </Box>
      <Box>
        <AchieveLink
          noLink
          track={{
            list_name: 'Pagination Event',
            click_id: `${mainProductConfig[siteEnv || 'achieve']?.['MainProduct']} | Pagination`,
            click_position: 'Pagination',
            click_text: 'Pagination',
            nav_link_section: 'Pagination',
            track_event: 'ui_click',
            event_action: 'ui_click',
            event_type: 'ui_click',
          }}
        >
          <Pagination
            className={'flex justify-center'}
            color="primary"
            count={articlesAuthor?.pageCount}
            onChange={handleChangePage}
            page={currentPage}
            data-testid="articles-author-list-pagination-controls"
          />
        </AchieveLink>
      </Box>
    </Section>
  )
}

export { AuthorArticlesPaginated }
